/**
 * Per-user UI preferences, persisted across sessions and devices.
 *
 * Stored at tenants/{tenantId}/careUserPreferences/{uid} with one field per
 * preference key (e.g. reportsFilters, billingReportCohort,
 * billingReviewFilters). Firestore rules restrict each doc to its own user.
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getEffectiveTenantId } from './tenantContext';

async function prefsRef() {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return null;
  return doc(db, 'tenants', tenantId, 'careUserPreferences', uid);
}

/**
 * Read one preference value.
 * @param {string} key
 * @returns {Promise<any>} undefined when unset or unavailable
 */
export async function getCareUserPreference(key) {
  try {
    const ref = await prefsRef();
    if (!ref) return undefined;
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data()[key] : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Save one preference value (fire-and-forget friendly; errors are swallowed —
 * preferences must never break the page they decorate).
 * @param {string} key
 * @param {any} value - JSON-serializable
 */
export async function saveCareUserPreference(key, value) {
  try {
    const ref = await prefsRef();
    if (!ref) return;
    await setDoc(ref, { [key]: value, updatedAt: serverTimestamp() }, { merge: true });
  } catch {
    /* ignore */
  }
}
